import React, { createContext, useContext, useState } from "react";
import { Alert, Snackbar } from "@mui/material";

const ToastContext = createContext();

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
}

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const showToast = (message, severity = "info", duration = 6000) => {
        const id = Date.now() + Math.random();
        const toast = { id, message, severity, duration };
        setToasts(prev => [...prev, toast]);

        if (duration > 0) {
            setTimeout(() => {
                removeToast(id);
            }, duration);
        }
    };

    const removeToast = (id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    const showSuccess = (message, duration) => showToast(message, "success", duration);
    const showError = (message, duration) => showToast(message, "error", duration);
    const showWarning = (message, duration) => showToast(message, "warning", duration);
    const showInfo = (message, duration) => showToast(message, "info", duration);

    return (
        <ToastContext.Provider value={{ showToast, showSuccess, showError, showWarning, showInfo }}>
            {children}
            {toasts.map((toast, index) => (
                <Snackbar
                    key={toast.id}
                    open={true}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    sx={{ 
                        mb: index * 7, // Stack multiple toasts
                    }}
                >
                    <Alert
                        onClose={() => removeToast(toast.id)}
                        severity={toast.severity}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {toast.message}
                    </Alert>
                </Snackbar>
            ))}
        </ToastContext.Provider>
    );
}
